import claimRegistry from "../../content/registry/claims.json";
import answerCardRegistry from "../../content/registry/answer-cards.json";
import experienceRegistry from "../../content/registry/experience.json";
import presentationRegistry from "../../content/registry/presentation.json";
import type { Citation, DepthMode, EvidenceMode } from "./types";

export const CONTENT_VERSION = claimRegistry.contentVersion;

export type ApprovedSource = Citation & {
  visibility: "public" | "generative-approved";
  text: string;
  keywords: string[];
};

export const approvedSources: ApprovedSource[] = claimRegistry.claims
  .filter(
    (claim) =>
      claim.approvalStatus === "approved" &&
      (claim.classification === "public" ||
        claim.classification === "generative-approved"),
  )
  .map((claim) => ({
    id: claim.claimId,
    title: claim.title,
    url: claim.sourceUrl ?? undefined,
    visibility: claim.classification as ApprovedSource["visibility"],
    text: claim.statement,
    keywords: claim.keywords,
  }));

export type AnswerCard = {
  id: string;
  label: string;
  canonicalPrompt: string;
  aliases: string[];
  answer: Record<DepthMode, string>;
  citations: string[];
  supportedEvidence: EvidenceMode[];
};

export const answerCards = answerCardRegistry.answerCards as AnswerCard[];

export const quickPrompts = answerCards.map(
  ({ id, label, canonicalPrompt }) => ({ id, label, prompt: canonicalPrompt }),
);

function keywordsFromText(value: string): string[] {
  return [
    ...new Set(
      value
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, " ")
        .split(/\s+/)
        .filter((word) => word.length > 3),
    ),
  ].slice(0, 36);
}

const approvedAnswerCardSources: ApprovedSource[] = answerCards.map((card) => ({
  id: `approved-answer-${card.id}`,
  title: `Approved answer: ${card.label}`,
  visibility: "generative-approved",
  text: [
    `Canonical prompt: ${card.canonicalPrompt}`,
    `Aliases: ${card.aliases.join(", ")}`,
    `Brief: ${card.answer.brief}`,
    `Standard: ${card.answer.auto}`,
    `Detailed: ${card.answer.deep}`,
  ].join("\n"),
  keywords: keywordsFromText(
    `${card.label} ${card.canonicalPrompt} ${card.aliases.join(" ")} ${card.answer.auto}`,
  ),
}));

const approvedPresentationSources: ApprovedSource[] = [
  {
    id: "approved-footprint-snapshot",
    title: "Approved footprint snapshot",
    visibility: "generative-approved",
    text: [
      presentationRegistry.mediaSnapshot.title,
      presentationRegistry.mediaSnapshot.summary,
      ...presentationRegistry.mediaStats.map(
        (stat) => `${stat.label}: ${stat.value}. ${stat.note}`,
      ),
    ].join("\n"),
    keywords: keywordsFromText(
      `${presentationRegistry.mediaSnapshot.title} ${presentationRegistry.mediaSnapshot.summary} ${presentationRegistry.mediaStats.map((stat) => `${stat.label} ${stat.value} ${stat.note}`).join(" ")}`,
    ),
  },
  // Keep the component-AI context compact; the full 50-item cache is for client-side simulation.
  ...presentationRegistry.executiveSummaryVariants
    .slice(0, 3)
    .map((summary, index) => ({
      id: `approved-summary-${index + 1}`,
      title: `Approved executive summary ${index + 1}`,
      visibility: "generative-approved" as const,
      text: [summary.lead, ...summary.highlights].join("\n"),
      keywords: keywordsFromText(
        [summary.lead, ...summary.highlights].join(" "),
      ),
    })),
  ...presentationRegistry.keyInsightCards.map((card, index) => ({
    id: `approved-insight-${index + 1}`,
    title: `Approved insight: ${card.title}`,
    visibility: "generative-approved" as const,
    text: `${card.title}\n${card.description}`,
    keywords: keywordsFromText(`${card.title} ${card.description}`),
  })),
  ...presentationRegistry.mediaSnippets.map((snippet, index) => ({
    id: `approved-media-snippet-${index + 1}`,
    title: `Approved media snippet: ${snippet.title}`,
    visibility: "generative-approved" as const,
    text: `${snippet.title}\n${snippet.summary}`,
    keywords: keywordsFromText(`${snippet.title} ${snippet.summary}`),
  })),
];

// Experience_v1 remains unchanged until the planned Case Studies redesign. These sets only govern
// the derivative AI context, so historical presentation data can remain intact until that approved
// migration includes its own content-correction pass.
const legacyCloudflarePortfolioCaseStudyIds = new Set([
  "ddos-protection-portfolio",
  "magic-transit-enterprise-growth",
  "security-analytics-reporting",
  "customer-trust-operations",
  "threat-intelligence-public-leadership",
]);

// This story is not approved for the new public narrative. Keep it out of AI retrieval as well as
// out of the future Experience_v2/Case Studies plan, without changing the existing Experience_v1 UI.
const excludedLegacyExperienceCaseStudyIds = new Set(["china-network-service"]);

const approvedExperienceSources: ApprovedSource[] =
  experienceRegistry.caseStudies
    .filter(
      (caseStudy) => !excludedLegacyExperienceCaseStudyIds.has(caseStudy.id),
    )
    .map((caseStudy, index) => {
      // Keep the legacy current-tense AI-agent case study from reintroducing a false current-employment
      // signal into RAG, and align the concluded portfolio period with the new canonical timeline.
      const isConcludedAiRole = caseStudy.id === "ai-security-agents";
      const isConcludedCloudflarePortfolio =
        legacyCloudflarePortfolioCaseStudyIds.has(caseStudy.id);
      const period = isConcludedAiRole
        ? "January 2026 to July 2026"
        : isConcludedCloudflarePortfolio
          ? "2019 to January 2026"
          : caseStudy.period;
      const headline = isConcludedAiRole
        ? "Cloudflare's Cybersecurity Agent Platform"
        : caseStudy.headline;
      const recruiterSummary = isConcludedAiRole
        ? "Omer shaped the vision, strategy, priority use cases, and prioritisation for Cloudflare's Cybersecurity Agent Platform."
        : caseStudy.recruiterSummary;
      const ownership = isConcludedAiRole
        ? "As a Senior Product Manager, Omer set product direction for how agents, MCP servers, and supporting capabilities fit together for cybersecurity customers."
        : caseStudy.ownership;
      const whatIDid = isConcludedAiRole
        ? [
            "Set the product vision, strategy, priority use cases, and prioritisation for a coherent cybersecurity agent platform.",
            "Partnered across four teams and 20+ engineers; this was cross-functional product scope, not direct implementation ownership or direct reports.",
            "Kept clear summaries, guardrails, permission-aware execution, and human control central to the product experience.",
          ]
        : caseStudy.whatIDid;
      const outcomes = isConcludedAiRole
        ? caseStudy.outcomes.map((outcome) =>
            outcome.value === "Current role"
              ? { ...outcome, value: "January–July 2026 role" }
              : outcome,
          )
        : caseStudy.outcomes;
      const workstreams = isConcludedAiRole
        ? [
            {
              id: "cybersecurity-agent-platform",
              title: "Cybersecurity Agent Platform",
              description:
                "Product direction for how agent capabilities fit together into coherent cybersecurity customer experiences.",
            },
          ]
        : caseStudy.workstreams;
      const status = isConcludedAiRole
        ? "Employment status: Omer's Cloudflare role concluded on 31 July 2026; this case study is historical."
        : null;

      return {
        id: `approved-experience-${index + 1}`,
        title: `Approved experience: ${headline}`,
        visibility: "generative-approved",
        text: [
          period,
          headline,
          recruiterSummary,
          `Ownership: ${ownership}`,
          `What Omer did: ${whatIDid.join(" ")}`,
          `Stakeholders: ${caseStudy.stakeholders.join(", ")}`,
          `Evidence: ${outcomes.map((outcome) => `${outcome.value} ${outcome.label}. Scope: ${outcome.scope}`).join(" ")}`,
          `Workstreams: ${workstreams.map((workstream) => `${workstream.title}: ${workstream.description}`).join(" ")}`,
          status,
        ]
          .filter(Boolean)
          .join("\n"),
        keywords: keywordsFromText(
          `${headline} ${recruiterSummary} ${ownership} ${whatIDid.join(" ")} ${caseStudy.stakeholders.join(" ")} ${outcomes.map((outcome) => `${outcome.value} ${outcome.label}`).join(" ")} ${workstreams.map((workstream) => `${workstream.title} ${workstream.description}`).join(" ")}`,
        ),
      };
    });

export const approvedGenerativeSources: ApprovedSource[] = [
  ...approvedSources,
  ...approvedAnswerCardSources,
  ...approvedPresentationSources,
  ...approvedExperienceSources,
];

// The exit explanation is intentionally available to direct AI questions, but should not become
// a default homepage/component theme while its broader website treatment is being designed.
const componentExcludedSourceIds = new Set([
  "cloudflare-employment-transition-2026",
  "cloudflare-workforce-reduction-context-2026",
  "approved-answer-current-work",
]);

export const approvedComponentSources = approvedGenerativeSources.filter(
  (source) => !componentExcludedSourceIds.has(source.id),
);

// Resume generation uses only the latest sanitized CV. The broader assistant corpus includes
// historical portfolio and case-study detail that is unnecessary for a public downloadable CV.
export const approvedResumeSources = approvedAnswerCardSources.filter(
  (source) => source.id === "approved-answer-cv",
);
